(() => {
  const GLOBAL_KEY = '__SPRPLX_MUSIC_PLAYER__';
  const STORAGE_KEY = 'sprplxMusicPlayerState:v1';
  const SESSION_KEY = 'sprplxMusicPlayerSession:v1';
  const PLAY_EVENT = 'sprplx:music-player:play';

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].scan();
    return;
  }

  const instances = new Map();

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const formatTime = (seconds) => {
    const safeSeconds = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
    const minutes = Math.floor(safeSeconds / 60);
    const remainder = String(safeSeconds % 60).padStart(2, '0');
    return `${minutes}:${remainder}`;
  };

  const readStorage = () => {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}');
    } catch (_error) {
      return {};
    }
  };

  const writeStorage = (state) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (_error) {
      // Playback remains functional when storage is unavailable.
    }
  };

  const readSession = () => {
    try {
      return JSON.parse(window.sessionStorage.getItem(SESSION_KEY) || '{}');
    } catch (_error) {
      return {};
    }
  };

  const writeSession = (state) => {
    try {
      window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
    } catch (_error) {
      // Playback remains functional when session storage is unavailable.
    }
  };

  class SprplxMusicPlayer {
    constructor(root) {
      this.root = root;
      this.id = root.dataset.sectionId || `sprplx-${Date.now()}`;
      this.audio = root.querySelector('[data-sprplx-audio]');
      this.dialog = root.querySelector('[data-sprplx-dialog]');
      this.dock = root.querySelector('[data-sprplx-dock]');
      this.seek = root.querySelector('[data-sprplx-seek]');
      this.volume = root.querySelector('[data-sprplx-volume]');
      this.status = root.querySelector('[data-sprplx-status]');
      this.elapsed = root.querySelector('[data-sprplx-elapsed]');
      this.duration = root.querySelector('[data-sprplx-duration]');
      this.playlist = root.querySelector('[data-sprplx-playlist]');
      this.teardowns = [];
      this.currentIndex = 0;
      this.lastFocus = null;
      this.dockFrame = 0;
      this.shuffleEnabled = root.dataset.shuffleTracks === 'true';
      this.shuffleOrder = [];
      this.shuffleCursor = 0;
      this.startOnEnter = root.dataset.startOnEnter === 'true';
      this.enterSelector = root.dataset.enterSelector || '[data-enter]';
      this.enterDelay = clamp(Number.parseInt(root.dataset.enterDelay, 10) || 0, 0, 10000);
      this.rememberPlayback = root.dataset.rememberPlayback === 'true';
      this.resumeNavigation = root.dataset.resumeNavigation === 'true';
      this.restorePosition = 0;
      this.resumeRequested = false;
      this.resumeDelay = 0;
      this.resumeTimer = 0;
      this.resumeScheduled = false;
      this.suspendingForNavigation = false;
      this.lastProgressSave = 0;
      this.userConsented = false;
      this.enterStartPending = false;
      this.enterStartAt = 0;
      this.enterStartPosition = 0;
      this.enterRestoreMuted = false;
      this.resumeRestoreMuted = null;
      this.mobileResumeTimer = 0;
      this.mobileAudioEnvironment = Boolean(
        window.matchMedia?.('(pointer: coarse)').matches || navigator.maxTouchPoints > 0
      );

      this.tracks = this.readTracks();

      if (!this.audio || !this.dialog || !this.dock || !this.tracks.length) {
        root.dataset.empty = 'true';
        return;
      }

      root.dataset.empty = 'false';
      this.supportsVolumeControl = this.detectVolumeControlSupport();
      root.dataset.volumeControl = this.supportsVolumeControl ? 'web' : 'device';
      this.volume.disabled = !this.supportsVolumeControl;
      this.restoreState();
      this.resetShuffleOrder(this.currentIndex);
      this.renderPlaylist();
      this.bindControls();
      this.bindAudio();
      this.bindEnterTrigger();
      this.bindCoordination();
      this.bindDocking();
      this.loadTrack(this.currentIndex, false, { initial: true });
    }

    readTracks() {
      const dataElement = this.root.querySelector('[data-sprplx-tracks]');

      try {
        const tracks = JSON.parse(dataElement?.textContent || '[]');
        return tracks
          .filter((track) => track && track.audio)
          .map((track, index) => ({
            id: track.id || `track-${index}`,
            title: String(track.title || 'Untitled track'),
            artist: String(track.artist || 'Unknown artist'),
            artwork: String(track.artwork || ''),
            audio: String(track.audio || ''),
            duration: Number.parseFloat(track.duration) || 0
          }));
      } catch (_error) {
        return [];
      }
    }

    detectVolumeControlSupport() {
      const originalVolume = this.audio.volume;
      const testVolume = originalVolume > 0.5 ? 0.25 : 0.75;

      try {
        this.audio.volume = testVolume;
        const supported = Math.abs(this.audio.volume - testVolume) < 0.01;
        if (supported) this.audio.volume = originalVolume;
        return supported;
      } catch (_error) {
        return false;
      }
    }

    resetShuffleOrder(startIndex) {
      const remaining = this.tracks
        .map((_track, index) => index)
        .filter((index) => index !== startIndex);

      for (let index = remaining.length - 1; index > 0; index -= 1) {
        const randomIndex = Math.floor(Math.random() * (index + 1));
        [remaining[index], remaining[randomIndex]] = [remaining[randomIndex], remaining[index]];
      }

      this.shuffleOrder = [startIndex, ...remaining];
      this.shuffleCursor = 0;
    }

    restoreState() {
      const stored = readStorage();
      const session = readSession();
      const now = Date.now();
      const defaultVolume = Number.parseFloat(this.volume?.value || '0.7');
      const storedVolume = Number.parseFloat(stored.volume);
      const preferredVolume = clamp(Number.isFinite(storedVolume) ? storedVolume : defaultVolume, 0, 1);
      const sessionIsRecent = now - (Number.parseFloat(session.updatedAt) || 0) < 30 * 60 * 1000;

      this.currentIndex = clamp(Number.parseInt(stored.trackIndex, 10) || 0, 0, this.tracks.length - 1);
      const sessionMatchesTrack = Number.parseInt(session.trackIndex, 10) === this.currentIndex;
      const sessionPosition = Number.parseFloat(session.currentTime);
      const storedPosition = Number.parseFloat(stored.currentTime);
      const pendingStart = sessionIsRecent && Number.parseFloat(session.startAt) > now - 10000;

      if (sessionIsRecent && typeof session.shuffleEnabled === 'boolean') {
        this.shuffleEnabled = session.shuffleEnabled;
      }

      this.userConsented = Boolean(stored.userConsented || (sessionIsRecent && session.userConsented));
      this.restorePosition = this.rememberPlayback
        ? Math.max(0, sessionMatchesTrack && Number.isFinite(sessionPosition) ? sessionPosition : storedPosition || 0)
        : 0;
      this.resumeRequested = Boolean(
        this.rememberPlayback &&
        this.resumeNavigation &&
        this.userConsented &&
        sessionIsRecent &&
        (session.wasPlaying || pendingStart)
      );
      this.resumeDelay = pendingStart ? Math.max(0, Number.parseFloat(session.startAt) - now) : 0;
      if (this.supportsVolumeControl) this.audio.volume = preferredVolume;
      this.audio.muted = Boolean(stored.muted);
      this.lastAudibleVolume = preferredVolume > 0 ? preferredVolume : clamp(defaultVolume || 0.7, 0.05, 1);
      this.volume.value = String(this.supportsVolumeControl ? this.audio.volume : preferredVolume);
    }

    saveState(options = {}) {
      const currentTime = Number.isFinite(options.currentTime)
        ? options.currentTime
        : Number.isFinite(this.audio.currentTime) ? this.audio.currentTime : 0;
      const wasPlaying = typeof options.wasPlaying === 'boolean' ? options.wasPlaying : this.isPlaying();
      const existingSession = readSession();
      const startAt = Object.prototype.hasOwnProperty.call(options, 'startAt')
        ? Number.parseFloat(options.startAt) || 0
        : Number.parseFloat(existingSession.startAt) || 0;
      const persistentMuted = this.enterStartPending
        ? this.enterRestoreMuted
        : typeof this.resumeRestoreMuted === 'boolean' ? this.resumeRestoreMuted : this.audio.muted;

      writeStorage({
        trackIndex: this.currentIndex,
        volume: this.supportsVolumeControl ? this.audio.volume : Number.parseFloat(this.volume.value) || 0.7,
        muted: persistentMuted,
        currentTime: this.rememberPlayback ? currentTime : 0,
        userConsented: this.userConsented
      });

      writeSession({
        trackIndex: this.currentIndex,
        currentTime: this.rememberPlayback ? currentTime : 0,
        wasPlaying: this.rememberPlayback ? wasPlaying : false,
        userConsented: this.userConsented,
        shuffleEnabled: this.shuffleEnabled,
        startAt,
        updatedAt: Date.now()
      });
    }

    on(target, eventName, handler, options) {
      target?.addEventListener(eventName, handler, options);
      this.teardowns.push(() => target?.removeEventListener(eventName, handler, options));
    }

    bindControls() {
      this.root.querySelectorAll('[data-sprplx-shuffle]').forEach((button) => {
        this.on(button, 'click', () => this.toggleShuffle());
      });

      this.root.querySelectorAll('[data-sprplx-play]').forEach((button) => {
        this.on(button, 'click', () => this.togglePlayback(true));
      });

      this.root.querySelectorAll('[data-sprplx-previous]').forEach((button) => {
        this.on(button, 'click', () => this.previous());
      });

      this.root.querySelectorAll('[data-sprplx-next]').forEach((button) => {
        this.on(button, 'click', () => this.next(this.isPlaying()));
      });

      this.root.querySelectorAll('[data-sprplx-expand]').forEach((button) => {
        this.on(button, 'click', () => this.openDialog(button));
      });

      this.root.querySelectorAll('[data-sprplx-close]').forEach((button) => {
        this.on(button, 'click', () => this.dialog.close());
      });

      this.root.querySelectorAll('[data-sprplx-mute]').forEach((button) => {
        this.on(button, 'click', () => this.toggleMute());
      });

      this.on(this.seek, 'input', () => {
        if (Number.isFinite(this.audio.duration)) {
          this.audio.currentTime = clamp(Number.parseFloat(this.seek.value) || 0, 0, this.audio.duration);
          this.saveState();
        }
      });

      this.on(this.volume, 'input', () => {
        if (!this.supportsVolumeControl) return;
        this.audio.volume = clamp(Number.parseFloat(this.volume.value) || 0, 0, 1);
        if (this.audio.volume > 0) {
          this.audio.muted = false;
          this.lastAudibleVolume = this.audio.volume;
        }
        this.saveState();
        this.updateVolumeUI();
      });

      this.on(this.dialog, 'close', () => {
        this.root.dataset.expanded = 'false';
        this.lastFocus?.focus({ preventScroll: true });
      });

      this.on(this.dialog, 'click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });

      this.on(this.root, 'keydown', (event) => this.handleKeyboard(event));
    }

    bindEnterTrigger() {
      if (!this.startOnEnter || !this.enterSelector) return;

      try {
        document.querySelector(this.enterSelector);
      } catch (_error) {
        return;
      }

      this.on(document, 'click', (event) => {
        const trigger = event.target?.closest?.(this.enterSelector);
        if (!trigger) return;

        this.beginDelayedEnterPlayback();
      }, { capture: true });
    }

    beginDelayedEnterPlayback() {
      this.userConsented = true;

      if (this.resumeTimer) window.clearTimeout(this.resumeTimer);
      if (this.enterDelay === 0) {
        this.play(true);
        return;
      }

      this.enterStartPending = true;
      this.enterStartAt = Date.now() + this.enterDelay;
      this.enterStartPosition = Number.isFinite(this.audio.currentTime) ? this.audio.currentTime : 0;
      this.enterRestoreMuted = this.audio.muted;
      this.saveState({ wasPlaying: false, startAt: this.enterStartAt });

      // Safari requires play() inside the original tap. Keep it silent until the requested delay ends.
      this.audio.muted = true;
      this.resumeTimer = window.setTimeout(() => this.finishDelayedEnterPlayback(), this.enterDelay);
      this.play(true);
    }

    finishDelayedEnterPlayback() {
      this.resumeTimer = 0;
      if (!this.enterStartPending) return;

      const restoreMuted = this.enterRestoreMuted;
      const restorePosition = this.enterStartPosition;
      this.enterStartPending = false;
      this.enterStartAt = 0;

      try {
        if (Number.isFinite(restorePosition) && this.audio.readyState > 0) {
          this.audio.currentTime = restorePosition;
        }
      } catch (_error) {
        // The original gesture-initiated play promise will continue once media is ready.
      }

      this.audio.muted = restoreMuted;
      this.updateVolumeUI();

      if (this.audio.paused) {
        this.play(true);
      } else {
        this.saveState({ wasPlaying: true, startAt: 0 });
      }
    }

    cancelDelayedEnterPlayback() {
      if (!this.enterStartPending) return;
      if (this.resumeTimer) window.clearTimeout(this.resumeTimer);
      this.resumeTimer = 0;
      this.enterStartPending = false;
      this.enterStartAt = 0;
      this.audio.muted = this.enterRestoreMuted;
      this.updateVolumeUI();
    }

    bindAudio() {
      this.on(this.audio, 'loadedmetadata', () => {
        const track = this.tracks[this.currentIndex];
        if (track && Number.isFinite(this.audio.duration)) track.duration = this.audio.duration;
        this.restorePlaybackAfterLoad();
        this.updateProgress();
        this.updatePlaylistDurations();
      });

      this.on(this.audio, 'durationchange', () => this.updateProgress());
      this.on(this.audio, 'timeupdate', () => {
        this.updateProgress();
        if (this.rememberPlayback && Date.now() - this.lastProgressSave > 1500) {
          this.lastProgressSave = Date.now();
          this.saveState({ wasPlaying: this.isPlaying() });
        }
      });
      this.on(this.audio, 'play', () => {
        this.updatePlaybackUI();
        this.saveState({ wasPlaying: true, startAt: this.enterStartPending ? this.enterStartAt : 0 });
      });
      this.on(this.audio, 'pause', () => {
        this.updatePlaybackUI();
        if (!this.suspendingForNavigation) {
          this.cancelDelayedEnterPlayback();
          this.saveState({ wasPlaying: false, startAt: 0 });
        }
      });
      this.on(this.audio, 'volumechange', () => this.updateVolumeUI());
      this.on(this.audio, 'ended', () => this.next(true));
      this.on(this.audio, 'error', () => this.setStatus('This track could not be loaded.'));
      this.on(this.audio, 'waiting', () => this.setStatus('Loading track...'));
      this.on(this.audio, 'canplay', () => {
        this.setStatus('');
        this.restorePlaybackAfterLoad();
      });
    }

    restorePlaybackAfterLoad() {
      if (this.restorePosition > 0 && Number.isFinite(this.audio.duration)) {
        this.audio.currentTime = clamp(this.restorePosition, 0, Math.max(0, this.audio.duration - 0.25));
        this.restorePosition = 0;
        this.updateProgress();
      }

      if (!this.resumeRequested || this.resumeScheduled) return;
      this.resumeScheduled = true;
      if (this.resumeTimer) window.clearTimeout(this.resumeTimer);
      this.resumeTimer = window.setTimeout(() => {
        this.resumeTimer = 0;
        this.resumeRequested = false;
        this.resumePlayback();
      }, this.resumeDelay);
    }

    resumePlayback() {
      if (!this.mobileAudioEnvironment || this.audio.muted) {
        this.play(false);
        return;
      }

      this.resumeRestoreMuted = this.audio.muted;
      this.audio.muted = true;
      const playPromise = this.play(false);

      if (!playPromise?.then) {
        this.audio.muted = this.resumeRestoreMuted;
        this.resumeRestoreMuted = null;
        return;
      }

      playPromise
        .then(() => {
          this.mobileResumeTimer = window.setTimeout(() => {
            const restoreMuted = this.resumeRestoreMuted;
            this.resumeRestoreMuted = null;
            this.mobileResumeTimer = 0;
            this.audio.muted = Boolean(restoreMuted);
            this.updateVolumeUI();
            this.saveState({ wasPlaying: this.isPlaying(), startAt: 0 });
          }, 0);
        })
        .catch(() => {
          this.audio.muted = Boolean(this.resumeRestoreMuted);
          this.resumeRestoreMuted = null;
          this.updateVolumeUI();
        });
    }

    bindCoordination() {
      this.on(window, PLAY_EVENT, (event) => {
        if (event.detail?.id !== this.id) this.audio.pause();
      });

      this.on(window, 'pagehide', () => {
        const wasPlaying = this.isPlaying();
        this.suspendingForNavigation = true;
        this.saveState({ wasPlaying, currentTime: this.audio.currentTime });
        this.audio.pause();
      });

      this.on(window, 'pageshow', (event) => {
        this.suspendingForNavigation = false;
        if (!event.persisted || !this.rememberPlayback || !this.resumeNavigation) return;

        const session = readSession();
        if (session.wasPlaying && session.userConsented) this.resumePlayback();
      });
    }

    bindDocking() {
      const requestSync = () => {
        if (this.dockFrame) return;
        this.dockFrame = window.requestAnimationFrame(() => {
          this.dockFrame = 0;
          this.syncDock();
        });
      };

      this.requestDockSync = requestSync;
      this.on(window, 'scroll', requestSync, { passive: true });
      this.on(document, 'scroll', requestSync, { capture: true, passive: true });
      this.on(document.body, 'scroll', requestSync, { passive: true });
      this.on(window, 'resize', requestSync, { passive: true });
      this.on(window.visualViewport, 'resize', requestSync, { passive: true });
      this.on(window.visualViewport, 'scroll', requestSync, { passive: true });
      this.syncDock();
    }

    syncDock() {
      if (this.root.dataset.dockAtFooter !== 'true') {
        this.root.classList.remove('is-docked');
        this.root.style.height = '0px';
        return;
      }

      const dockHeight = this.dock.getBoundingClientRect().height || 74;
      const computedBottom = Number.parseFloat(window.getComputedStyle(this.dock).bottom);
      const bottomGap = Number.isFinite(computedBottom) ? computedBottom : 12;
      const anchorHeight = 10 + dockHeight + bottomGap;
      this.root.style.height = `${anchorHeight}px`;

      const scrollTop = Math.max(
        window.scrollY,
        document.scrollingElement?.scrollTop || 0,
        document.documentElement.scrollTop,
        document.body.scrollTop
      );
      const anchorTop = this.root.getBoundingClientRect().top + scrollTop;
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const visibleBottom = scrollTop + (window.visualViewport?.offsetTop || 0) + viewportHeight;
      const threshold = anchorTop + anchorHeight;

      this.root.classList.toggle('is-docked', visibleBottom + 1 >= threshold);
    }

    handleKeyboard(event) {
      const tagName = event.target?.tagName?.toLowerCase();
      const isFormControl = ['button', 'input', 'textarea', 'select', 'a'].includes(tagName);

      if ((event.key === 'm' || event.key === 'M') && tagName !== 'input') {
        event.preventDefault();
        this.toggleMute();
        return;
      }

      if (isFormControl) return;

      if (event.key === ' ') {
        event.preventDefault();
        this.togglePlayback(true);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.seekBy(-5);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.seekBy(5);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.changeVolume(0.05);
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.changeVolume(-0.05);
      }
    }

    renderPlaylist() {
      if (!this.playlist) return;

      const fragment = document.createDocumentFragment();

      this.tracks.forEach((track, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'sprplx-music-track';
        button.dataset.trackIndex = String(index);
        button.setAttribute('role', 'listitem');
        button.setAttribute('aria-label', `Play ${track.title} by ${track.artist}`);
        button.innerHTML = `
          <span class="sprplx-music-cover">
            <img src="${this.escapeAttribute(track.artwork)}" alt="" width="40" height="40" loading="lazy">
            <span class="sprplx-music-waveform" aria-hidden="true">
              ${Array.from({ length: 7 }, (_, waveIndex) => `<i style="--wave-index: ${waveIndex + 1}"></i>`).join('')}
            </span>
          </span>
          <span class="sprplx-music-track__meta">
            <span class="sprplx-music-marquee sprplx-music-track__title"><span>${this.escapeHtml(track.title)}</span></span>
            <span class="sprplx-music-marquee sprplx-music-track__artist"><span>${this.escapeHtml(track.artist)}</span></span>
          </span>
          <span class="sprplx-music-track__duration">${formatTime(track.duration)}</span>
        `;
        button.addEventListener('click', () => {
          this.userConsented = true;
          this.loadTrack(index, true, { userInitiated: true });
        });
        fragment.appendChild(button);
      });

      this.playlist.replaceChildren(fragment);
    }

    updatePlaylistDurations() {
      this.playlist?.querySelectorAll('[data-track-index]').forEach((row) => {
        const track = this.tracks[Number.parseInt(row.dataset.trackIndex, 10)];
        const duration = row.querySelector('.sprplx-music-track__duration');
        if (track && duration) duration.textContent = formatTime(track.duration);
      });
    }

    loadTrack(index, shouldPlay, options = {}) {
      const nextIndex = ((index % this.tracks.length) + this.tracks.length) % this.tracks.length;
      const track = this.tracks[nextIndex];
      const isNewTrack = nextIndex !== this.currentIndex || this.audio.src !== track.audio;

      if (this.shuffleEnabled && !options.fromShuffle) this.resetShuffleOrder(nextIndex);

      if (isNewTrack) {
        this.audio.pause();
        this.currentIndex = nextIndex;
        this.audio.src = track.audio;
        this.audio.load();
      }

      this.updateTrackUI();
      this.updateProgress();
      if (!options.initial) this.saveState({ wasPlaying: shouldPlay });

      if (shouldPlay) this.play(Boolean(options.userInitiated));
    }

    play(userInitiated = false) {
      if (userInitiated) this.userConsented = true;
      if (!this.audio.src) this.loadTrack(this.currentIndex, false);

      window.dispatchEvent(new CustomEvent(PLAY_EVENT, { detail: { id: this.id } }));
      try {
        const playPromise = this.audio.play();

        if (playPromise?.then) {
          playPromise
            .then(() => this.saveState({
              wasPlaying: true,
              startAt: this.enterStartPending ? this.enterStartAt : 0
            }))
            .catch(() => {
              this.setStatus('Press play to start audio.');
              this.saveState({
                wasPlaying: false,
                startAt: this.enterStartPending ? this.enterStartAt : 0
              });
            });
        }

        return playPromise;
      } catch (_error) {
        this.setStatus('Press play to start audio.');
        this.saveState({ wasPlaying: false, startAt: 0 });
        return null;
      }
    }

    togglePlayback(userInitiated = false) {
      if (this.audio.paused || this.audio.ended) {
        this.play(userInitiated);
      } else {
        this.audio.pause();
      }
    }

    previous() {
      if (this.audio.currentTime > 4) {
        this.audio.currentTime = 0;
        return;
      }

      if (this.shuffleEnabled) {
        if (this.shuffleCursor === 0) return;
        this.shuffleCursor -= 1;
        this.loadTrack(this.shuffleOrder[this.shuffleCursor], this.isPlaying(), { fromShuffle: true });
        return;
      }

      this.loadTrack(this.currentIndex - 1, this.isPlaying());
    }

    next(shouldPlay = this.isPlaying()) {
      if (this.shuffleEnabled && this.tracks.length > 1) {
        if (this.shuffleCursor >= this.shuffleOrder.length - 1) this.resetShuffleOrder(this.currentIndex);
        this.shuffleCursor += 1;
        this.loadTrack(this.shuffleOrder[this.shuffleCursor], shouldPlay, { fromShuffle: true });
        return;
      }

      this.loadTrack(this.currentIndex + 1, shouldPlay);
    }

    toggleShuffle() {
      this.shuffleEnabled = !this.shuffleEnabled;
      this.resetShuffleOrder(this.currentIndex);
      this.saveState();
      this.updateShuffleUI();
    }

    seekBy(seconds) {
      if (!Number.isFinite(this.audio.duration)) return;
      this.audio.currentTime = clamp(this.audio.currentTime + seconds, 0, this.audio.duration);
    }

    changeVolume(amount) {
      if (!this.supportsVolumeControl) return;
      this.audio.volume = clamp(this.audio.volume + amount, 0, 1);
      this.audio.muted = false;
      if (this.audio.volume > 0) this.lastAudibleVolume = this.audio.volume;
      this.volume.value = String(this.audio.volume);
      this.saveState();
      this.updateVolumeUI();
    }

    toggleMute() {
      if (this.audio.muted || (this.supportsVolumeControl && this.audio.volume === 0)) {
        this.audio.muted = false;
        if (this.supportsVolumeControl && this.audio.volume === 0) {
          this.audio.volume = this.lastAudibleVolume || 0.7;
        }
      } else {
        if (this.supportsVolumeControl) this.lastAudibleVolume = this.audio.volume;
        this.audio.muted = true;
      }

      if (this.supportsVolumeControl) this.volume.value = String(this.audio.volume);
      this.saveState();
      this.updateVolumeUI();
    }

    openDialog(trigger) {
      this.lastFocus = trigger || document.activeElement;
      this.root.dataset.expanded = 'true';

      if (!this.dialog.open) this.dialog.showModal();
      this.root.querySelector('[data-sprplx-close]')?.focus({ preventScroll: true });
      this.updateMarquees();
    }

    isPlaying() {
      return !this.audio.paused && !this.audio.ended;
    }

    updateTrackUI() {
      const track = this.tracks[this.currentIndex];
      if (!track) return;

      this.root.querySelectorAll('[data-sprplx-mini-title], [data-sprplx-dialog-title]').forEach((element) => {
        element.textContent = track.title;
      });

      this.root.querySelectorAll('[data-sprplx-mini-artist], [data-sprplx-dialog-artist]').forEach((element) => {
        element.textContent = track.artist;
      });

      this.setArtwork(this.root.querySelector('[data-sprplx-mini-artwork]'), track);
      this.setArtwork(this.root.querySelector('[data-sprplx-dialog-artwork]'), track);

      this.playlist?.querySelectorAll('[data-track-index]').forEach((row) => {
        const active = Number.parseInt(row.dataset.trackIndex, 10) === this.currentIndex;
        row.dataset.active = String(active);
        row.setAttribute('aria-current', active ? 'true' : 'false');
      });

      this.updatePlaybackUI();
      this.updateVolumeUI();
      this.updateShuffleUI();
      this.updateMarquees();
    }

    setArtwork(image, track) {
      if (!image) return;

      if (track.artwork) {
        image.src = track.artwork;
        image.alt = `${track.title} artwork`;
      } else {
        image.removeAttribute('src');
        image.alt = '';
      }
    }

    updatePlaybackUI() {
      const playing = this.isPlaying();
      this.root.dataset.playing = String(playing);

      this.root.querySelectorAll('[data-sprplx-play]').forEach((button) => {
        button.setAttribute('aria-label', playing ? 'Pause' : 'Play');
      });

      this.root.querySelectorAll('[data-sprplx-play-icon]').forEach((icon) => {
        icon.textContent = playing ? 'II' : '▶';
      });

      this.playlist?.querySelectorAll('[data-track-index]').forEach((row) => {
        const active = Number.parseInt(row.dataset.trackIndex, 10) === this.currentIndex;
        row.dataset.playing = String(active && playing);
      });

      if (playing) this.setStatus('');
    }

    updateProgress() {
      const duration = Number.isFinite(this.audio.duration)
        ? this.audio.duration
        : this.tracks[this.currentIndex]?.duration || 0;
      const currentTime = Number.isFinite(this.audio.currentTime) ? this.audio.currentTime : 0;

      this.seek.max = String(duration);
      this.seek.value = String(clamp(currentTime, 0, duration || 0));
      this.seek.setAttribute('aria-valuetext', `${formatTime(currentTime)} of ${formatTime(duration)}`);
      this.elapsed.textContent = formatTime(currentTime);
      this.duration.textContent = formatTime(duration);
    }

    updateVolumeUI() {
      const muted = this.audio.muted || (this.supportsVolumeControl && this.audio.volume === 0);
      if (this.supportsVolumeControl) this.volume.value = String(this.audio.volume);
      this.root.querySelectorAll('[data-sprplx-mute]').forEach((button) => {
        button.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
      });
      this.root.querySelectorAll('[data-sprplx-mute-icon]').forEach((icon) => {
        icon.textContent = muted ? 'MUTE' : 'VOL';
      });
    }

    updateShuffleUI() {
      this.root.dataset.shuffleEnabled = String(this.shuffleEnabled);
      this.root.querySelectorAll('[data-sprplx-shuffle]').forEach((button) => {
        button.setAttribute('aria-pressed', String(this.shuffleEnabled));
        button.setAttribute('aria-label', this.shuffleEnabled ? 'Turn shuffle off' : 'Turn shuffle on');
      });
    }

    updateMarquees() {
      window.requestAnimationFrame(() => {
        this.root.querySelectorAll('.sprplx-music-marquee > span').forEach((text) => {
          text.dataset.overflow = 'false';
          text.style.removeProperty('--sprplx-marquee-distance');

          const containerWidth = text.parentElement?.clientWidth || 0;
          const distance = Math.ceil(text.scrollWidth - containerWidth);

          if (distance > 4) {
            text.style.setProperty('--sprplx-marquee-distance', `${distance}px`);
            text.dataset.overflow = 'true';
          }
        });
      });
    }

    setStatus(message) {
      if (this.status) this.status.textContent = message;
    }

    escapeHtml(value) {
      const element = document.createElement('span');
      element.textContent = value;
      return element.innerHTML;
    }

    escapeAttribute(value) {
      return this.escapeHtml(value)
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;')
        .replaceAll('`', '&#96;');
    }

    destroy() {
      this.audio?.pause();
      this.teardowns.forEach((teardown) => teardown());
      if (this.dockFrame) window.cancelAnimationFrame(this.dockFrame);
      if (this.resumeTimer) window.clearTimeout(this.resumeTimer);
      if (this.mobileResumeTimer) window.clearTimeout(this.mobileResumeTimer);
      if (this.dialog?.open) this.dialog.close();
      instances.delete(this.root);
      delete this.root.dataset.sprplxInitialized;
    }
  }

  const initialize = (root) => {
    if (instances.has(root) || root.dataset.sprplxInitialized === 'true') return;
    root.dataset.sprplxInitialized = 'true';
    instances.set(root, new SprplxMusicPlayer(root));
  };

  const scan = (scope = document) => {
    if (scope.matches?.('[data-sprplx-music-player]')) initialize(scope);
    scope.querySelectorAll?.('[data-sprplx-music-player]').forEach(initialize);
  };

  window[GLOBAL_KEY] = { scan };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('[data-sprplx-music-player]')
      ? event.target
      : event.target.querySelector?.('[data-sprplx-music-player]');
    instances.get(root)?.destroy();
  });
})();
